/** The signed-in member, their partner and role, for every page. */
import { createContext, useContext } from "react";
import { worksAlerts, type Role } from "../../shared/access";

export type Me = {
  member: { id: string; email: string; name: string; role: Role; tenant_scope: { all?: boolean; tags?: string[]; tenants?: string[] }; must_change: number };
  partner: { id: string; msp_id: string; name: string; domain: string; tier: string; status: string; branding: { name?: string; accent?: string; logo?: string } };
  role: { key: Role; label: string; business: boolean; team: boolean; tenants: boolean };
  via: string;
  /** The newest platform release the Brain recorded (empty before the first; `unavailable` when the Brain did not answer), and this portal's own commit and build time. */
  version: { version: string; build: string; unavailable?: boolean; commit: string; built_at: string };
  environment: string;
};

/** Fired on window after a tenant is connected or disconnected. */
export const PORTFOLIO_CHANGED = "msp:portfolio-changed";

export const MeCtx = createContext<Me | null>(null);

export function useMe(): Me {
  const me = useContext(MeCtx);
  if (!me) throw new Error("useMe outside the signed-in app");
  return me;
}

/** Owner and Admin manage; business roles see money. The Worker enforces the same rules; these only shape the pages. */
export function useCan() {
  const me = useMe();
  const r = me.member.role;
  return {
    manage: r === "owner" || r === "admin",
    owner: r === "owner",
    business: me.role.business,
    tenants: me.role.tenants,
    reports: me.role.business || r === "owner" || r === "admin",
    /** Take, release, resolve, snooze and ticket alerts. */
    alerts: worksAlerts(r),
    /** Send reports and change who gets them. */
    sendReports: r === "owner" || r === "admin" || r === "account_manager",
    /** Change a tenant's contract and monthly report. */
    contract: r === "owner" || r === "admin" || r === "account_manager",
  };
}
